import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs';
import {GenderData} from '../../../../../data-repositories/gender-data';
import {MaritalData} from '../../../../../data-repositories/marital-data';
import {SalutationTypeData} from '../../../../../data-repositories/salutation-type-data';
import {StatusPair} from '../../../../../models/status-pair';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {FocusInvalidService} from '../../../../../services/focus-invalid.service';
import {IPersonalInfo} from '../../../../../models/personal-info';
import {ActivatedRoute} from '@angular/router';
import {StagesNavigationService} from '../../../stages-navigation/stages-navigation.service';
import {forbiddenValues, validateOptional, validateOptionalRequired} from '../../../../../services/validators';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-personal',
  templateUrl: './personal.component.html',
  styleUrls: ['./personal.component.scss']
})
export class PersonalComponent implements OnInit, OnDestroy {
  public genderList: Array<StatusPair>;
  public salutationTypeList: Array<string>;
  public dateDay: number;
  public dateMonth: number;
  public dateYear: number;
  public personalForm: FormGroup;
  public submitted: boolean;
  @ViewChild('personalStage') personalFormElement: ElementRef;
  maritalStatusList: StatusPair[];

  @Input() requiredPhone = false;
  @Input() forbiddenPhones: Array<string> = new Array<string>();
  @Input() forbiddenEmails: Array<string> = new Array<string>();
  @Input() isSecondFolder = false;

  @Output() userModelChange = new EventEmitter<IPersonalInfo>();
  private emailRe = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  private $subscription: Subscription;
  private formSub = new Subscription();

  constructor(
    genderData: GenderData,
    salutationTypeData: SalutationTypeData,
    maritalStatusData: MaritalData,
    private formBuilder: FormBuilder,
    private validationService: ValidationInfoService,
    public toastr: ToastrService,
    private focusInvalidService: FocusInvalidService,
    private navigationService: StagesNavigationService,
    route: ActivatedRoute) {

    this.genderList = genderData.getGenderList();
    this.maritalStatusList = maritalStatusData.getMaritalStatus();
    this.salutationTypeList = salutationTypeData.getSalutationTypeList();
    this.submitted = false;
  }

  private _userModel: IPersonalInfo;

  get userModel(): IPersonalInfo {
    return this._userModel;
  }

  @Input()
  set userModel(value: IPersonalInfo) {
    this._userModel = value;
    this.patchFormFromModel();
    this.initialize();
  }

  ngOnInit() {
    this.buildForm();
    this.patchFormFromModel();
    this.formSub.add(this.personalForm.valueChanges.subscribe(() => this.syncModelFromForm()));
    this.submitted = false;

    this.$subscription = this.validationService.onUserUpdate.subscribe(() => {
      this.submitted = true;

      this.onDateChanged();
      this.validationService.markAllFieldsAsTouched(this.personalForm);
      this.onDateChanged();
      this.validationService.markAllFieldsAsTouched(this.personalForm);

      this.focusInvalidService.selectInvalidInput(this.personalFormElement);

      this.validationService.validate(
        this.personalForm.valid &&
        this.isDate() &&
        this.isCorrectAge() &&
        this.isCorrectYear(), 'PersonalComponent');
    });

    this.subscribeAllInputsOnUpdate();
  }

  onFirstNameChanged() {
    if (this.userModel.firstName) {
      this.userModel.firstName = this.userModel.firstName.trim();
    }
  }

  onLastNameChanged() {
    if (this.userModel.lastName) {
      this.userModel.lastName = this.userModel.lastName.trim();
    }
  }

  onNativeFirstNameChanged() {
    if (this.userModel.nativeFirstName) {
      this.userModel.nativeFirstName = this.userModel.nativeFirstName.trim();
    }
  }

  onNativeLastNameChanged() {
    if (this.userModel.nativeLastName) {
      this.userModel.nativeLastName = this.userModel.nativeLastName.trim();
    }
  }

  public isRequiredField(fieldName: string): boolean {
    return this.personalForm.controls[fieldName].invalid
      && ((this.personalForm.controls[fieldName].dirty || this.personalForm.controls[fieldName].touched) && this.submitted)
      && this.personalForm.controls[fieldName].errors.required;
  }

  public hasErrorField(fieldName: string, error: string): boolean {
    const control = this.personalForm.controls[fieldName];
    return control.invalid &&
      ((control.dirty || control.touched) || this.submitted) &&
      control.hasError(error);
  }

  ngOnDestroy() {
    this.$subscription.unsubscribe();
    this.formSub.unsubscribe();
    this.dateDay = 0;
    this.dateMonth = 0;
    this.dateYear = 0;
    this.submitted = false;
  }

  onDateChanged() {
    if (!this.isDate()) {
      this.personalForm.controls.mmDate.setErrors({invalidDate: true});
      this.personalForm.controls.ddDate.setErrors({invalidDate: true});
      this.personalForm.controls.yyyyDate.setErrors({invalidDate: true});
    } else if (!this.isCorrectAge()) {
      this.personalForm.controls.mmDate.setErrors({incorrectAge: true});
      this.personalForm.controls.ddDate.setErrors({incorrectAge: true});
      this.personalForm.controls.yyyyDate.setErrors({incorrectAge: true});
    } else if (!this.isCorrectYear()) {
      this.personalForm.controls.mmDate.setErrors({incorrectYear: true});
      this.personalForm.controls.ddDate.setErrors({incorrectYear: true});
      this.personalForm.controls.yyyyDate.setErrors({incorrectYear: true});
    } else if (!this.dateMonth || !this.dateDay || !this.dateYear) {
      this.personalForm.controls.mmDate.setErrors(this.dateMonth ? null : {required: true});
      this.personalForm.controls.ddDate.setErrors(this.dateDay ? null : {required: true});
      this.personalForm.controls.yyyyDate.setErrors(this.dateYear ? null : {required: true});
    } else {
      this.personalForm.controls.mmDate.setErrors(null);
      this.personalForm.controls.ddDate.setErrors(null);
      this.personalForm.controls.yyyyDate.setErrors(null);

      this.userModel.dateOfBirth = (Number(this.dateDay) < 10 ? '0' : '') + (Number(this.dateDay)) +
        '/' + (Number(this.dateMonth) < 10 ? '0' : '') + Number(this.dateMonth) + '/'
        + this.dateYear;

      this.updateUser();
    }
  }

  isDate(): boolean {
    if (!this.dateDay || !this.dateMonth || !this.dateYear) {
      return true;
    }

    const d = new Date(this.dateYear, this.dateMonth - 1, this.dateDay);
    return d.getFullYear() === this.dateYear && d.getMonth() + 1 === this.dateMonth && d.getDate() === this.dateDay;
  }

  isCorrectYear(): boolean {
    if (!this.dateYear || !this.dateMonth || !this.dateDay) {
      return true;
    }
    return this.dateYear >= 1900;
  }

  isCorrectAge(): boolean {
    if (!this.dateYear || !this.dateMonth || !this.dateDay) {
      return true;
    }

    const age = this.calculateAge(this.dateYear, this.dateMonth, this.dateDay);

    return age >= 18;
  }

  isCashDOB() {
    if (!this.dateYear || !this.dateMonth || !this.dateDay) {
      return false;
    }

    const age = this.calculateAge(this.dateYear, this.dateMonth, this.dateDay);

    return age >= 18 && age < 21;
  }

  subscribeAllInputsOnUpdate() {
    const personalStage = document.getElementById('personalStage');

    const inputs = personalStage.getElementsByTagName('input');
    for (let inputIndex = 0; inputIndex < inputs.length; inputIndex++) {
      inputs.item(inputIndex).addEventListener('change', () => {
        this.updateUser();
      });
    }
  }

  public updateUser(): void {
    this.userModelChange.emit(this.userModel);
  }

  private calculateAge(year: number, month: number, day: number): number {
    const dob = new Date(year, month - 1, day);
    const today = new Date();

    let age = today.getFullYear() - dob.getFullYear();

    const birthdayPassed =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() &&
        today.getDate() >= dob.getDate());

    if (!birthdayPassed) {
      age--;
    }

    return age;
  }

  private initialize() {
    if (this.userModel.dateOfBirth) {
      const splitedDate = this.userModel.dateOfBirth.split('/');
      const day = Number(splitedDate[0]);
      const month = Number(splitedDate[1]);
      const year = Number(splitedDate[2]);

      if (day > 0) {
        this.dateDay = day;
        this.dateMonth = month;
        this.dateYear = year;
      }
    }

    this.onFirstNameChanged();
    this.onLastNameChanged();
  }

  private buildForm() {
    this.personalForm = this.formBuilder.group({
      firstName: new FormControl('', [Validators.required]),
      nativeFirstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      nativeLastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(this.emailRe),
        forbiddenValues(this.forbiddenEmails)
      ]),
      mmDate: new FormControl('', [Validators.required]),
      ddDate: new FormControl('', [Validators.required]),
      yyyyDate: new FormControl('', [Validators.required]),
      gender: new FormControl('', [Validators.required]),
      phone: new FormControl('', [
        validateOptional(() => this.requiredPhone, [
          Validators.required,
          forbiddenValues(this.forbiddenPhones),
        ]),
      ]),
      maritalStatus: new FormControl('', [Validators.required]),
      numberOfDependents: new FormControl('', [Validators.required])
    });
  }

  private patchFormFromModel(): void {
    if (!this._userModel || !this.personalForm) {
      return;
    }

    this.personalForm.patchValue({
      firstName: this._userModel.firstName ?? '',
      lastName: this._userModel.lastName ?? '',
      nativeFirstName: this._userModel.nativeFirstName ?? '',
      nativeLastName: this._userModel.nativeLastName ?? '',
      email: this._userModel.email ?? '',
      gender: this._userModel.gender ?? '',
      phone: this._userModel.phone ?? '',
      maritalStatus: this._userModel.maritalStatus ?? '',
      numberOfDependents: this._userModel.numberOfDependents ?? ''
    }, {emitEvent: false});
    formLog.state('Personal', this._userModel, this.personalForm);
  }

  private syncModelFromForm(): void {
    if (!this._userModel) {
      return;
    }

    const value = this.personalForm.getRawValue();
    this._userModel.firstName = value.firstName ? value.firstName.trim() : value.firstName;
    this._userModel.lastName = value.lastName ? value.lastName.trim() : value.lastName;
    this._userModel.nativeFirstName = value.nativeFirstName ? value.nativeFirstName.trim() : value.nativeFirstName;
    this._userModel.nativeLastName = value.nativeLastName ? value.nativeLastName.trim() : value.nativeLastName;
    this._userModel.email = value.email;
    this._userModel.gender = value.gender;
    this._userModel.phone = value.phone;
    this._userModel.maritalStatus = value.maritalStatus;
    this._userModel.numberOfDependents = value.numberOfDependents;
    formLog.state('Personal', this._userModel, this.personalForm);
  }
}
